/**
 * Mapping Session Module
 *
 * Handles state management and operations for document mapping sessions
 *
 * Provides the following features:
 * - Source and target document management
 * - Suggestion handling (AI and accepted)
 * - Active node tracking
 * - Content transfer operations
 * - Editor mode management
 *
 * @module MappingSession
 */

import { makeAutoObservable, runInAction, action } from 'mobx';
import { fetchSourceFiles, getMappingSession } from '../services/apiCalls';
import {
  AIResponse,
  GeneralizationSuggestion,
  MappingTypeEnum,
  SaveSuggestionPayload,
} from '../types';
import { WebSocketMessage } from '../types/websocket';
import {
  SmartEditorType,
  SmartSelectedTextMap,
  SmartSelectedTextRange,
  TargetNodeMap,
} from '../components/SmartEditor';
import { first, isEmpty, toNumber } from 'lodash';
import WebSocketClient from '../services/websocket';
import { autoAcceptSuggestion } from '../services/api';
import { notifyError, notifySuccess } from '../helpers/utils';

/**
 * Response type for mapping session API
 *
 * @interface MappingSessionResponse
 * @property {Array<{id: string | number}>} t2 - Array of mapping targets
 * @property {AIResponse} aiSuggestions - AI generated suggestions
 * @property {GeneralizationSuggestion[]} acceptedSuggestions - List of accepted suggestions
 * @property {MappingTypeEnum} mappingType - Type of mapping session
 */
interface MappingSessionResponse {
  t2: Array<{ id: string | number }>;
  aiSuggestions: AIResponse;
  acceptedSuggestions: GeneralizationSuggestion[];
  mappingType: MappingTypeEnum;
}

/**
 * Editor operation modes
 * Controls available actions and UI state
 *
 * @enum {string}
 * @property {string} TRANSFER - Data transfer between source and target
 * @property {string} EDITING - Content editing and formatting
 */
export enum EditorMode {
  TRANSFER = 'transfer',
  EDITING = 'editing',
}

/**
 * Client information structure
 *
 * @interface ClientInfo
 * @property {string} userId - Unique identifier for the client
 * @property {string} username - Display name of the client
 */
export interface ClientInfo {
  userId: string;
  username: string;
}

/**
 * WebSocket update message structure
 * Contains suggestions data from other users
 *
 * @interface UpdateMessageData
 * @property {Array<GeneralizationSuggestion & { mappedBy: Object }>} suggestions - Array of suggestions with mapper info
 * @property {Object} suggestions[].mappedBy - Information about user who made the suggestion
 * @property {number} suggestions[].mappedBy.id - Unique identifier of the mapper
 * @property {string} suggestions[].mappedBy.firstName - First name of the mapper
 * @property {string} suggestions[].mappedBy.lastName - Last name of the mapper
 */
export interface UpdateMessageData {
  suggestions: Array<
    GeneralizationSuggestion & {
      mappedBy: {
        id: number;
        firstName: string;
        lastName: string;
      };
    }
  >;
}

/**
 * Mapping Session Interface
 *
 * @interface IMappingSessionStore
 * @property {number} sourceFileId - Currently selected mapping value
 * @property {boolean} isLoading - Loading state of the mapping session
 * @property {ClientInfo} clientInfo - Current user's client information
 * @property {Error} error - Error state if any
 * @property {number} sessionId - Current mapping session identifier
 * @property {MappingTypeEnum} mappingType - Type of the current mapping
 * @property {AIResponse} aiSuggestions - AI generated mapping suggestions
 * @property {GeneralizationSuggestion[]} acceptedSuggestions - List of accepted mapping suggestions
 * @property {EditorMode} editorMode - Current editor operation mode
 * @property {boolean} canTransfer - Whether content transfer is currently allowed
 * @property {Element[]} transferQueue - Queue of elements waiting to be transferred
 * @property {Array<{id: string | number}>} sourceFiles - List of available source files
 * @property {Record<string, ClientInfo>} lockedNodes - Map of node IDs to users who locked them
 * @property {WebSocketClient} webSocketClient - WebSocket client for real-time updates
 * @property {Map<SmartEditorType, string>} selectedText - Map of editor types to their selected text
 * @property {Map<SmartEditorType, Range>} selectedTextRange - Map of editor types to their selection ranges
 * @property {UpdateMessageData} [updatedMessageData] - Optional data for update messages
 * @property {string[]} sourceNodeMap - List of source node IDs with suggestions
 * @property {Maps of suggestions by node IDs} suggestionMaps - Maps of suggestions by node IDs
 *
 * @method setSourceFileId - Update source file ID and fetch content
 * @method setEditorMode - Update editor operation mode
 * @method clearTransfer - Clear transfer queue
 * @method setActiveNode - Set active node for editor type
 * @method setActiveContent - Update content for editor type
 * @method setIsLoading - Update loading state
 * @method getElementFromContent - Get element from editor content
 * @method getSuggestionBySourceId - Find suggestion by source node ID
 * @method getSuggestionByTargetId - Find suggestion by target node ID
 * @method lockNode - Lock a node for editing
 */
export interface IMappingSessionStore {
  sourceFileId?: number;
  isLoading: boolean;
  clientInfo: ClientInfo;
  error?: Error;
  sessionId?: number;
  mappingType?: MappingTypeEnum;
  aiSuggestions?: AIResponse;
  acceptedSuggestions?: GeneralizationSuggestion[];
  derivedSourceNodeId: string;
  activeNodeIds: Map<SmartEditorType, string[]>;
  activeContent: Map<SmartEditorType, string | undefined>;
  editorDocument: Map<SmartEditorType, Document | undefined>;
  selectedText: Map<SmartEditorType, string>;
  selectedTextRange: Map<SmartEditorType, Range>;
  editorMode: EditorMode;
  canTransfer: boolean;
  transferQueue: Element[];
  sourceFiles?: Array<{ id: string | number }>;
  webSocketClient?: WebSocketClient;
  lockedNodes: Record<string, ClientInfo>;
  updatedMessageData?: UpdateMessageData;
  sourceNodeMap: string[];
  targetNodeMap: TargetNodeMap;
  suggestionMaps: {
    targetNodeIdSuggestionMap: Record<string, GeneralizationSuggestion>;
    sourceNodeIdSuggestionMap: Record<string, GeneralizationSuggestion>;
  };

  /** Baseline document states for comparison (represents last saved state) */
  baselineDocuments: Map<SmartEditorType, Document | undefined>;

  /** Incremental change tracking - records changes as they happen */
  documentChanges: Map<SmartEditorType, Record<string, { old: string; updated: string }>>;

  // Methods
  setupData: (data: MappingSessionResponse) => void;
  setSourceFileId: (
    sourceId: number,
    isOriginal?: boolean,
    options?: { bypassDocumentLock?: boolean },
  ) => void;
  /** When true, keep the current source file (SSOT); block switches to other source docs */
  lockSourceDocumentSelection: boolean;
  setLockSourceDocumentSelection: (locked: boolean) => void;
  setEditorMode: (mode: EditorMode) => void;
  clearTransfer: () => void;
  setActiveNode: (type: SmartEditorType, nodeId: string, isMultiSelect?: boolean) => void;
  setActiveContent: (type: SmartEditorType, html: string) => void;
  setIsLoading: (isLoading: boolean) => void;
  getElementsFromContent: (type: SmartEditorType) => Element[] | null;
  getSuggestionBySourceId: (
    sourceNodeId: string,
  ) =>
    | GeneralizationSuggestion
    | (Partial<GeneralizationSuggestion> & { targetNodeId: string })
    | undefined;
  getSuggestionByTargetId: (
    targetNodeId: string,
  ) =>
    | GeneralizationSuggestion
    | (Partial<GeneralizationSuggestion> & { sourceNodeId: string })
    | undefined;
  lockNode: (nodeId: string) => void;
  unlockNode: (nodeId: string) => void;
  addTextSelection: (type: SmartEditorType, text: string) => void;
  addTextSelectionRange: (type: SmartEditorType, range: Range) => void;
  getSelectedContent: () => SmartSelectedTextMap | undefined;
  getSelectedRange: () => SmartSelectedTextRange | undefined;
  clearTextSelection: () => void;
  setSourceNodeMap: (sourceNodeMap: string[]) => void;
  getSourceNodeIds: (targetNodeId: string) => string[];
  getSourceCatalogId: (targetNodeId: string, sourceNodeId?: string) => number | undefined;
  isLocked: (nodeId: string) => boolean;
  getActiveNodeIds: (type: SmartEditorType) => string[];
  handleAutoFill: () => void;

  // Baseline document management methods
  setBaselineDocument: (type: SmartEditorType, document: Document) => void;
  getBaselineDocument: (type: SmartEditorType) => Document | undefined;
  hasDocumentChanges: (type: SmartEditorType) => boolean;
  getDocumentChanges: (type: SmartEditorType) => Record<string, { old: string; updated: string }>;
  resetToBaseline: (type: SmartEditorType) => void;
  updateBaselineAfterSave: (type: SmartEditorType) => void;
  setAcceptedSuggestions: (suggestions: GeneralizationSuggestion[]) => void;
  /** Replace accepted list and rebuild maps used by Smart Editor (snapping, highlights). */
  syncAcceptedSuggestionsFromSave: (suggestions: GeneralizationSuggestion[]) => void;
  setDerivedSourceNodeId: (nodeId: string, isMultiSelect: boolean) => void;

  // Incremental change tracking methods
  recordNodeChange: (
    type: SmartEditorType,
    nodeId: string,
    oldValue: string,
    newValue: string,
  ) => void;
  clearDocumentChanges: (type: SmartEditorType) => void;
  clearTrackedNodeChange: (type: SmartEditorType, nodeId: string) => void;
  getTrackedChanges: (type: SmartEditorType) => Record<string, { old: string; updated: string }>;

  // Save preparation methods
  handleSave: (type: SmartEditorType) => Promise<SaveSuggestionPayload[]>;
}

export class MappingSessionStore implements IMappingSessionStore {
  /** Currently selected mapping value */
  sourceFileId: number | undefined;

  /**
   * Mirrors authoring "Lock" on the source file: when true, target cell selection must not
   * switch which source document is loaded (Smart Editor updates source via setSourceFileId).
   */
  lockSourceDocumentSelection = false;

  /** List of source node IDs with suggestions */
  sourceNodeMap: string[] = [];

  /** Add new property to store prepared map */
  targetNodeMap: TargetNodeMap = {};

  /** Loading state of the mapping session */
  isLoading = true;

  /** Current user's client information */
  clientInfo: ClientInfo;

  /** Error state if any */
  error?: Error;

  /** ID of the current mapping session */
  sessionId?: number;

  /** Type of the current mapping */
  mappingType?: MappingTypeEnum;

  /** AI generated mapping suggestions */
  aiSuggestions?: AIResponse;

  /** List of accepted mapping suggestions */
  acceptedSuggestions?: GeneralizationSuggestion[];

  /** Current editor operation mode */
  editorMode: EditorMode = EditorMode.TRANSFER;

  /** List of available source files with their IDs */
  sourceFiles?: Array<{ id: string | number }>;

  /** Map of active node IDs for each editor type (source/target) */
  activeNodeIds = new Map<SmartEditorType, string[]>();

  derivedSourceNodeId = '';

  /** Map of current HTML content for each editor type */
  activeContent = new Map<SmartEditorType, string | undefined>();

  editorDocument = new Map<SmartEditorType, Document | undefined>();

  /** Map of editor types to their selected text */
  selectedText = new Map<SmartEditorType, string>();

  /** Map of editor types to their selection ranges */
  selectedTextRange = new Map<SmartEditorType, Range>();

  /** Map of node IDs to users who locked them */
  lockedNodes: Record<string, ClientInfo> = {};

  /** Queue of elements waiting to be transferred between editors */
  transferQueue: Element[] = [];

  /** WebSocket client for real-time updates */
  webSocketClient?: WebSocketClient;

  /** Optional data for update messages */
  updatedMessageData?: UpdateMessageData;

  /** Parser for converting HTML strings into DOM documents */
  private domParser = new DOMParser();

  /** Cache storing parsed documents to avoid repeated parsing of same content */
  private docCache = new Map<SmartEditorType, { html: string; doc: Document }>();

  private targetNodeIdSuggestionMap: Record<string, GeneralizationSuggestion> = {};
  private sourceNodeIdSuggestionMap: Record<string, GeneralizationSuggestion> = {};

  /** Baseline document states for comparison (represents last saved state) */
  baselineDocuments = new Map<SmartEditorType, Document | undefined>();

  /** Incremental change tracking - records changes as they happen */
  documentChanges = new Map<SmartEditorType, Record<string, { old: string; updated: string }>>();

  /**
   * Creates an instance of MappingSessionStore and initializes it
   */
  constructor(clientInfo: ClientInfo) {
    this.clientInfo = clientInfo;
    makeAutoObservable(this, {
      recordNodeChange: action,
      clearDocumentChanges: action,
    });
    this.initialize();
    this.setupCleanup();
  }

  /**
   * Check if transfer is possible
   * Validates editor mode and active selections for transfer
   *
   * @method canTransfer
   * @returns {boolean} Whether transfer is allowed
   *
   * @remarks
   * - Requires TRANSFER editor mode
   * - For full node transfer: requires both source and target nodes
   * - For partial transfer: requires target text + (source text OR source node)
   * - For node transfer: requires both nodes selected and equal selection lengths
   */
  get canTransfer() {
    // Must be in transfer mode
    if (this.editorMode !== EditorMode.TRANSFER) {
      return false;
    }

    const targetNodes = this.activeNodeIds.get('target') || [];
    const sourceNodes = this.activeNodeIds.get('source') || [];
    const targetTextRaw = this.selectedText.get('target');
    const sourceText = this.selectedText.get('source');
    // Multiple target cells + only whitespace in stored selection → treat as full-cell transfer
    const targetText =
      targetNodes.length > 1 && typeof targetTextRaw === 'string'
        ? targetTextRaw.trim()
        : targetTextRaw;

    // If target text is selected, need either source text or source nodes
    if (!isEmpty(targetText)) {
      return !isEmpty(sourceText) || !isEmpty(sourceNodes);
    }

    // For node transfer:
    // 1. Both selections must not be empty
    // 2. Must have same number of nodes selected
    return (
      !isEmpty(targetNodes) && !isEmpty(sourceNodes) && targetNodes.length === sourceNodes.length
    );
  }

  get suggestionMaps() {
    return {
      targetNodeIdSuggestionMap: this.targetNodeIdSuggestionMap,
      sourceNodeIdSuggestionMap: this.sourceNodeIdSuggestionMap,
    };
  }

  setSourceNodeMap = (sourceNodeMap: string[]) => {
    this.sourceNodeMap = sourceNodeMap;
  };

  addSourceNodeIds = (sourceNodeIds?: string[]) => {
    if (!sourceNodeIds || isEmpty(sourceNodeIds)) {
      return;
    }
    this.setSourceNodeMap([...this.sourceNodeMap, ...sourceNodeIds]);
  };

  /**
   * Set active node for specified editor type
   * Updates the active node tracking for source/target editors
   *
   * @method setActiveNode
   * @param {SmartEditorType} type - Editor type (source/target)
   * @param {string} nodeId - Node identifier to set as active
   * @param {boolean} [isMultiSelect=false] - Whether this is a multi-selection action
   * @returns {void}
   */
  setActiveNode = (type: SmartEditorType, nodeId: string, isMultiSelect = false) => {
    // Handle empty nodeId - clear selection
    if (nodeId === '') {
      this.activeNodeIds.set(type, []);
      return;
    }

    // Handle single selection
    if (!isMultiSelect) {
      this.activeNodeIds.set(type, [nodeId]);
      return;
    }

    const currentNodes = this.activeNodeIds.get(type) || [];
    const isNodeSelected = currentNodes.includes(nodeId);

    // Toggle node selection
    const updatedNodes = isNodeSelected
      ? currentNodes.filter((id) => id !== nodeId)
      : [...currentNodes, nodeId];

    this.activeNodeIds.set(type, updatedNodes);
  };

  setDerivedSourceNodeId = (nodeId: string, isMultiSelect = false) => {
    this.derivedSourceNodeId = nodeId;
    this.setActiveNode('source', nodeId, isMultiSelect);
  };

  setActiveContent = (type: SmartEditorType, html: string) => {
    if (type === 'source' && this.acceptedSuggestions?.length) {
      // Only parse and modify if we have suggestions to apply
      const modifiedHtml = this.applyAcceptedSuggestionsToHtml(html);
      this.activeContent.set(type, modifiedHtml);
    } else {
      this.activeContent.set(type, html);
    }
  };

  /**
   * Setup data for the mapping session
   * Updates store with provided mapping session data
   *
   * @method setupData
   * @param {MappingSessionResponse} data - Mapping session data to initialize
   * @returns {void}
   *
   * @example
   * store.setupData({
   *   t2: [{ id: 1 }],
   *   aiSuggestions: { ... },
   *   acceptedSuggestions: [],
   *   mappingType: MappingTypeEnum.AI_AUTHORING
   * });
   */
  setupData = (data: MappingSessionResponse) => {
    runInAction(() => {
      this.sourceFiles = data.t2;
      this.aiSuggestions = data.aiSuggestions;
      this.acceptedSuggestions = data.acceptedSuggestions;
      this.mappingType = data.mappingType;
      this.isLoading = false;

      // Prepare both maps immediately
      this.targetNodeMap = this.prepareTargetNodeMap(data.aiSuggestions, data.acceptedSuggestions);
      this.sourceNodeMap = this.prepareSourceNodeMap(data.aiSuggestions, data.acceptedSuggestions);
      this.setupSuggestionMaps();
      this.isLoading = false;
    });
  };

  handleAutoFill = async () => {
    if (!this.sessionId) {
      return;
    }

    try {
      const result = await autoAcceptSuggestion({ id: this.sessionId });
      this.acceptedSuggestions = [...(this.acceptedSuggestions || []), ...result.data.data];

      this.acceptedSuggestions.forEach((suggestion) => {
        if (!suggestion) {
          return;
        }
        this.setAutoFillElement(suggestion.targetValue, suggestion.targetNodeId);
      });
      notifySuccess('Auto Filled Successful');
    } catch (error) {
      notifyError('Unable to Auto Fill');
      console.error('Auto fill error:', error);
    }
  };

  getActiveNodeIds = (type: SmartEditorType) => {
    return this.activeNodeIds.get(type) ?? [];
  };

  getSourceNodeIds = (targetNodeId: string) => {
    // 1. First check accepted suggestions using the pre-built suggestion map
    const acceptedSuggestion = this.suggestionMaps.targetNodeIdSuggestionMap[targetNodeId];
    if (acceptedSuggestion) {
      return [acceptedSuggestion.sourceNodeId];
    }

    // 2. If no accepted suggestion, check AI suggestions using targetNodeMap
    const aiSuggestions = this.targetNodeMap[targetNodeId];
    if (aiSuggestions && aiSuggestions.length > 0) {
      // Get all source node IDs from the suggestions
      return aiSuggestions.map((suggestion) => Object.keys(suggestion)[0]);
    }

    return [];
  };

  getSourceCatalogId = (targetNodeId: string, sourceNodeId?: string) => {
    let sourceId: number | undefined;

    if (!this.sourceFileId) {
      sourceId = toNumber(first(this.sourceFiles)?.id);
    }

    if (!sourceNodeId) {
      return sourceId;
    }

    // 1. First check accepted suggestions
    const acceptedSuggestion = this.suggestionMaps.sourceNodeIdSuggestionMap[sourceNodeId];
    if (acceptedSuggestion?.sourceFile?.id) {
      sourceId = acceptedSuggestion.sourceFile.id;
    }

    // 2. Check AI suggestions
    const aiSuggestions = this.targetNodeMap[targetNodeId];
    if (aiSuggestions) {
      const matchingSuggestion = aiSuggestions.find(
        (suggestion) => Object.keys(suggestion)[0] === sourceNodeId,
      );
      const matchingSourceId = matchingSuggestion?.[sourceNodeId].sourceFileId;
      if (matchingSourceId) {
        sourceId = matchingSourceId;
      }
    }

    return sourceId;
  };

  setLockSourceDocumentSelection = (locked: boolean) => {
    this.lockSourceDocumentSelection = locked;
  };

  setSourceFileId = async (
    sourceId: number,
    isOriginal = false,
    options?: { bypassDocumentLock?: boolean },
  ) => {
    if (
      !options?.bypassDocumentLock &&
      this.lockSourceDocumentSelection &&
      this.sourceFileId !== undefined &&
      sourceId !== this.sourceFileId
    ) {
      return;
    }

    this.sourceFileId = sourceId;
    this.setIsLoading(true);

    try {
      await this.fetchSourceFiles(isOriginal);
    } catch (error) {
      runInAction(() => {
        this.setError(error as Error);
      });
    }
  };

  /**
   * Update editor operation mode
   * Controls available actions and UI state
   *
   * @method setEditorMode
   * @param {EditorMode} mode - New editor mode
   */
  setEditorMode = (mode: EditorMode) => {
    this.editorMode = mode;
  };

  /**
   * Set loading state for the mapping session
   * Updates the loading indicator for UI feedback
   *
   * @method setIsLoading
   * @param {boolean} isLoading - Whether the session is loading
   * @returns {void}
   */
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  /**
   * Clear transfer state
   * Resets transfer elements
   *
   * @method clearTransfer
   * @returns {void}
   */
  clearTransfer = () => {
    this.transferQueue = [];
  };

  /**
   * Get suggestion by source node ID
   * Searches through accepted and AI suggestions to find matching source node
   *
   * @method getSuggestionBySourceId
   * @param {string} sourceNodeId - Source node identifier to search for
   * @returns {GeneralizationSuggestion | undefined} Matching suggestion or undefined if not found
   *
   * @example
   * const suggestion = store.getSuggestionBySourceId("node-123");
   */
  getSuggestionBySourceId(sourceNodeId: string) {
    // Check accepted suggestions first
    const existingAcceptedSuggestion = this.acceptedSuggestions?.find(
      (s) => s.sourceNodeId === sourceNodeId,
    );
    if (existingAcceptedSuggestion) return existingAcceptedSuggestion;

    // Then check AI suggestions
    if (!this.aiSuggestions) return;

    for (const [targetNodeId, suggestions] of Object.entries(this.aiSuggestions)) {
      const suggestion = suggestions.find((s) => Object.keys(s)[0] === sourceNodeId);
      if (suggestion) {
        return { targetNodeId, ...suggestion[sourceNodeId] };
      }
    }
  }

  /**
   * Get suggestion by target node ID
   * Searches through accepted and AI suggestions to find matching target node
   *
   * @method getSuggestionByTargetId
   * @param {string} targetNodeId - Target node identifier to search for
   * @returns {GeneralizationSuggestion | undefined} Matching suggestion or undefined if not found
   *
   * @example
   * const suggestion = store.getSuggestionByTargetId("node-456");
   */
  getSuggestionByTargetId(targetNodeId: string) {
    // Check accepted suggestions first
    const acceptedSuggestion = this.acceptedSuggestions?.find(
      (s) => s.targetNodeId === targetNodeId,
    );
    if (acceptedSuggestion) return acceptedSuggestion;

    // Then check AI suggestions
    const suggestions = this.aiSuggestions?.[targetNodeId]?.[0];
    if (!suggestions) return;

    const sourceNodeId = Object.keys(suggestions)[0];
    return { sourceNodeId, ...suggestions[sourceNodeId] };
  }

  /**
   * Get elements from editor content by type
   * Retrieves DOM element from cached or newly parsed document
   *
   * @method getElementsFromContent
   * @param {SmartEditorType} type - Editor type to get content from
   * @returns {Element[] | null} Matching elements or null if not found
   * @throws {Error} When HTML parsing fails
   *
   * @example
   * const element = store.getElementsFromContent("source");
   */
  getElementsFromContent(type: SmartEditorType): Element[] | null {
    const nodeIds = this.activeNodeIds.get(type);
    const doc = this.editorDocument.get(type);

    if (isEmpty(nodeIds) || !doc) {
      return null;
    }

    const elements: Element[] = [];
    nodeIds?.forEach((nodeId) => {
      const selectorElement = doc?.querySelector(`[data-nodeid="${nodeId}"]`);
      if (selectorElement) {
        elements.push(selectorElement);
      }
    });
    return elements;
  }

  /**
   * Lock a node for editing
   * Unlocks any previously locked node and locks the new one
   *
   * @param {string} nodeId - ID of the node to lock
   * @returns {void}
   * @throws {Error} When WebSocket connection fails
   *
   * @example
   * store.lockNode("node-123");
   */
  lockNode = (nodeId: string) => {
    const { userId, username } = this.clientInfo;

    // Lock the new node
    this.webSocketClient?.lockNode(nodeId, userId, username);
  };

  /**
   * Check if a node is locked by another user
   *
   * @param nodeId The ID of the node to check
   * @returns {boolean} True if the node is locked by another user, false otherwise
   *
   * @example
   * // Check if a node is locked by another user
   * const isNodeLocked = store.isLocked("node-123");
   */
  isLocked = (nodeId: string): boolean => {
    const lockInfo = this.lockedNodes[nodeId];
    return !!lockInfo && lockInfo.userId !== this.clientInfo.userId;
  };

  /**
   * Add text selection to mapping session store
   * Updates selected text for specified editor type
   *
   * @method addTextSelection
   * @param {SmartEditorType} type - Editor type ('source' or 'target')
   * @param {string} text - Selected text content
   * @returns {void}
   *
   * @example
   * // Store source selection
   * store.addTextSelection('source', 'selected text');
   *
   * @example
   * // Store target selection
   * store.addTextSelection('target', 'highlighted content');
   *
   * @remarks
   * - Used in conjunction with addTextSelectionRange
   * - Part of partial text selection feature
   * - Maintains selection state for transfer operations
   */
  addTextSelection = (type: SmartEditorType, text: string) => {
    this.selectedText.set(type, text);
  };

  /**
   * Add selection range to mapping session store
   * Updates selection range for specified editor type
   *
   * @method addTextSelectionRange
   * @param {SmartEditorType} type - Editor type ('source' or 'target')
   * @param {Range} range - DOM Range object representing the selection
   * @returns {void}
   *
   * @example
   * // Store source range
   * const range = editor.selection.getRng();
   * store.addTextSelectionRange('source', range);
   *
   * @example
   * // Store target range
   * store.addTextSelectionRange('target', selectionRange);
   *
   * @remarks
   * - Used in conjunction with addTextSelection
   * - Stores precise selection position information
   * - Required for accurate partial text transfer
   * - Maintains DOM Range objects for exact text replacement
   */
  addTextSelectionRange = (type: SmartEditorType, range: Range) => {
    this.selectedTextRange.set(type, range);
  };

  /**
   * Clear all text selections
   * Resets both text and range selections for all editors
   *
   * @method clearTextSelection
   * @returns {void}
   *
   * @example
   * // Clear all selections
   * store.clearTextSelection();
   *
   * @remarks
   * - Resets both selectedText and selectedTextRange maps
   * - Called when selections are cancelled or cleared
   * - Used during editor mode changes
   * - Ensures clean state for new selections
   * - Important for maintaining consistent selection state
   */
  getSelectedContent = () => {
    const selectedTarget = this.selectedText.get('target');
    const selectedSource = this.selectedText.get('source');

    if (isEmpty(selectedTarget) || isEmpty(selectedSource)) {
      return;
    }

    return {
      target: selectedTarget,
      source: selectedSource,
    } as SmartSelectedTextMap;
  };

  setAcceptedSuggestions = (suggestions: GeneralizationSuggestion[]) => {
    this.acceptedSuggestions = [...(this.acceptedSuggestions || []), ...suggestions];
  };

  syncAcceptedSuggestionsFromSave = (suggestions: GeneralizationSuggestion[]) => {
    runInAction(() => {
      this.acceptedSuggestions = suggestions;
      this.targetNodeMap = this.prepareTargetNodeMap(this.aiSuggestions, this.acceptedSuggestions);
      this.sourceNodeMap = this.prepareSourceNodeMap(this.aiSuggestions, this.acceptedSuggestions);
      this.setupSuggestionMaps();
    });
  };

  /**
   * Get selected content from both editors
   * Retrieves current text selections from source and target editors
   *
   * @method getSelectedContent
   * @returns {SmartSelectedTextMap | undefined} Object containing selected text from both editors, undefined if either selection is empty
   *
   * @example
   * // When both editors have selections
   * const selections = store.getSelectedContent();
   * // Returns: { source: "selected source text", target: "selected target text" }
   *
   * @example
   * // When either selection is missing
   * const selections = store.getSelectedContent();
   * // Returns: undefined
   *
   * @remarks
   * - Returns undefined if either editor has no selection
   * - Used for partial text transfer operations
   * - Works in conjunction with getSelectedRange
   * - Part of the smart editor selection tracking system
   */
  getSelectedRange = () => {
    const selectedTargetRange = this.selectedTextRange.get('target');
    const selectedSourceRange = this.selectedTextRange.get('source');

    if (!selectedTargetRange || !selectedSourceRange) {
      return;
    }

    return {
      target: selectedTargetRange,
      source: selectedSourceRange,
    } as SmartSelectedTextRange;
  };

  clearTextSelection = () => {
    if (this.selectedText.size > 0) {
      this.selectedText = new Map<SmartEditorType, string>();
      this.selectedTextRange = new Map<SmartEditorType, Range>();
    }
  };

  clearActiveNodeId = () => {
    if (this.activeNodeIds.size > 0) {
      this.activeNodeIds = new Map<SmartEditorType, string[]>();
    }
  };

  /**
   * Initialize the mapping session
   * Extracts session ID from URL and fetches session data
   *
   * @private
   * @method initialize
   * @returns {void}
   * @throws {Error} When session ID is invalid
   */
  private initialize() {
    const match = window.location.pathname.match(/\/mapping-sessions\/(\d+)/);
    if (!match) {
      this.error = new Error('Invalid session ID in URL');
      this.isLoading = false;
      return;
    }

    this.sessionId = parseInt(match[1]);
    this.initializeWebSocket();

    // TODO: Call this after entire authoring page is refactored to new state management system
    // this.fetchMappingSession();
  }

  /**
   * Initialize WebSocket connection
   * Sets up real-time communication for the mapping session
   *
   * @private
   * @method initializeWebSocket
   * @returns {void}
   * @throws {Error} When session ID is missing
   */
  private initializeWebSocket() {
    if (this.sessionId) {
      const { userId, username } = this.clientInfo;
      this.webSocketClient = new WebSocketClient(
        this.sessionId.toString(),
        userId,
        username,
        this.handleWebSocketMessage,
      );
    }
  }

  /**
   * Handle incoming WebSocket messages
   * Routes different message types to appropriate handlers
   *
   * @private
   * @method handleWebSocketMessage
   * @param {Object} message - Incoming WebSocket message
   * @param {string} message.type - Type of message ('LOCKED_NODES' | 'UPDATE')
   * @param {Object} message.data - Message payload data
   * @returns {void}
   */
  private handleWebSocketMessage = (message: WebSocketMessage) => {
    switch (message.type) {
      case 'LOCKED_NODES':
        this.setLockedNodes(message.data as Record<string, ClientInfo>);
        break;
      case 'UPDATE':
        this.handleUpdateMessage(message.data as unknown as UpdateMessageData);
        break;
    }
  };

  /**
   * Handle incoming WebSocket messages
   * Processes different types of real-time updates
   *
   * @private
   * @method handleWebSocketMessage
   * @param {WebSocketMessage} message - Incoming message data
   * @returns {void}
   */
  private setLockedNodes = (nodes: Record<string, ClientInfo>) => {
    if (isEmpty(this.lockedNodes)) {
      this.clearTextSelection();
    }

    this.lockedNodes = nodes;
  };

  /**
   * Handle update messages from WebSocket
   * Updates target document with changes from other users
   *
   * @private
   * @method handleUpdateMessage
   * @param {UpdateMessageData} data - Update message data
   */
  private handleUpdateMessage = (data: UpdateMessageData) => {
    // Create a Map for faster lookups
    const nodeMap = new Map(
      data.suggestions.map((suggestion) => [suggestion.targetNodeId, suggestion]),
    );

    // Update the suggestion map
    this.targetNodeIdSuggestionMap = {
      ...this.targetNodeIdSuggestionMap,
      ...Object.fromEntries(nodeMap),
    };

    this.updatedMessageData = data;
  };

  private setAutoFillElement = (text = '', nodeId: string | null = null) => {
    if (!nodeId) {
      return;
    }

    const doc = this.editorDocument.get('target');
    const modifiedNodeId = nodeId?.replace('.html[0].body[1]', '') ?? '';
    let element = (doc?.querySelector(`[data-nodeid="${nodeId}"]`) ||
      doc?.querySelector(`[data-nodeid="${modifiedNodeId}"]`)) as HTMLElement;
    element = element.closest('td, th') || element;

    if (!element) {
      return;
    }

    if (element.tagName === 'IMG') {
      element.setAttribute('src', text);
      element.style.maxWidth = '500px';
      element.style.maxHeight = '300px';
      element.style.width = 'auto';
      element.style.height = 'auto';
    } else {
      element.innerHTML = text;
    }
    element.setAttribute('data-nodeid', nodeId);
  };

  /**
   * Unlock currently locked node
   * Releases lock on any node currently locked by this user
   *
   * @private
   * @method unlockNode
   * @returns {void}
   */
  unlockNode = (nodeId: string) => {
    const { userId, username } = this.clientInfo;
    this.webSocketClient?.unlockNode(nodeId, userId, username);
  };

  private unlockAllNodes = () => {
    const { userId, username } = this.clientInfo;
    const currentLock = Object.entries(this.lockedNodes).find(
      ([_, lockInfo]) => lockInfo.userId === userId,
    );

    if (currentLock) {
      const [currentNodeId] = currentLock;
      this.webSocketClient?.unlockNode(currentNodeId, userId, username);
    }
  };

  /**
   * Fetch mapping session data from the API
   *
   * @private
   * @method _fetchMappingSession
   * @returns {Promise<void>}
   * @throws {Error} When fetching session data fails
   */
  private async _fetchMappingSession() {
    if (!this.sessionId) return;

    try {
      const { data } = await getMappingSession(this.sessionId);
      this.setupData(data);
    } catch (error) {
      runInAction(() => {
        this.setError(error as Error);
      });
    }
  }

  /**
   * Fetch source files content from API
   * Retrieves HTML content for current source file ID
   *
   * @private
   * @method fetchSourceFiles
   * @returns {Promise<void>}
   * @throws {Error} When API request fails
   *
   * @example
   * // Internal usage
   * await this.fetchSourceFiles();
   */
  private async fetchSourceFiles(isOriginal: boolean) {
    if (!this.sourceFileId) return;

    const response = await fetchSourceFiles([this.sourceFileId], isOriginal);
    const { html } = response?.data?.[0] ?? {};

    if (isEmpty(html)) return;

    runInAction(() => {
      this.setActiveContent('source', html);
      this.setIsLoading(false);
    });
  }

  /**
   * Set error state and update loading
   * Updates error state and disables loading indicator
   *
   * @private
   * @method setError
   * @param {Error} error - Error to store
   * @returns {void}
   *
   * @example
   * // Internal usage
   * this.setError(new Error("Failed to fetch"));
   */
  private setError = (error: Error) => {
    this.error = error;
    this.setIsLoading(false);
  };

  /**
   * Setup cleanup handlers
   * Initializes event listeners for cleanup on page unload
   *
   * @private
   * @method setupCleanup
   * @returns {void}
   */
  private setupCleanup() {
    // Handle page unload/refresh
    window.addEventListener('beforeunload', this.unlockAllNodes);
  }

  private prepareTargetNodeMap(
    aiSuggestions?: AIResponse,
    acceptedSuggestions?: GeneralizationSuggestion[],
  ): TargetNodeMap {
    const targetNodeMap: TargetNodeMap = {};

    // Process AI suggestions
    Object.entries(aiSuggestions || {}).forEach(([targetNodeId, suggestions]) => {
      targetNodeMap[targetNodeId] = suggestions;
    });

    // Process accepted suggestions
    (acceptedSuggestions || [])
      .filter((s) => s.targetNodeId && s.sourceNodeId)
      .forEach((s) => {
        targetNodeMap[s.targetNodeId] = targetNodeMap[s.targetNodeId] || [];
        targetNodeMap[s.targetNodeId].push({
          [s.sourceNodeId]: {
            value: s.targetValue,
            sourceFileId: s.sourceFile?.id,
            positionX: s.positionX,
            positionY: s.positionY,
          },
        });
      });

    return targetNodeMap;
  }

  private prepareSourceNodeMap(
    aiSuggestions?: AIResponse,
    acceptedSuggestions?: GeneralizationSuggestion[],
  ): string[] {
    const sourceNodeIds = new Set<string>();

    // Process AI suggestions
    Object.values(aiSuggestions || {})
      .flat()
      .forEach((suggestion) => {
        const sourceNodeId = Object.keys(suggestion)[0];
        sourceNodeId && sourceNodeIds.add(sourceNodeId);
      });

    // Process accepted suggestions
    (acceptedSuggestions || [])
      .filter((s) => s.sourceNodeId)
      .forEach((s) => {
        if (s.sourceNodeId) {
          sourceNodeIds.add(s.sourceNodeId);
        }
      });

    return Array.from(sourceNodeIds);
  }

  private setupSuggestionMaps() {
    this.targetNodeIdSuggestionMap = {};
    this.sourceNodeIdSuggestionMap = {};

    this.acceptedSuggestions?.forEach((suggestion) => {
      if (suggestion.targetNodeId) {
        this.targetNodeIdSuggestionMap[suggestion.targetNodeId] = suggestion;
      }
      if (suggestion.sourceNodeId) {
        this.sourceNodeIdSuggestionMap[suggestion.sourceNodeId] = suggestion;
      }
    });
  }

  private applyAcceptedSuggestionsToHtml(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    this.acceptedSuggestions?.forEach((suggestion) => {
      const sourceFileDocNode = doc.querySelector(`[data-nodeid='${suggestion.sourceNodeId}']`);
      if (sourceFileDocNode && suggestion.sourceValue) {
        const tdElement = sourceFileDocNode.closest('td');
        const newNode = tdElement || sourceFileDocNode;
        newNode.setAttribute('data-nodeid', suggestion.sourceNodeId);
        newNode.innerHTML = suggestion.sourceValue;
      }
    });

    return doc.body.innerHTML;
  }

  /**
   * Cleanup store resources
   * Removes event listeners and unlocks nodes
   *
   * @method dispose
   * @returns {void}
   */
  dispose = () => {
    window.removeEventListener('beforeunload', this.unlockAllNodes);
    this.unlockAllNodes();
    this.lockSourceDocumentSelection = false;
  };

  // Baseline document management methods
  setBaselineDocument = (type: SmartEditorType, document: Document) => {
    // Create a deep copy to prevent mutation of the baseline
    const clonedDoc = document.cloneNode(true) as Document;
    this.baselineDocuments.set(type, clonedDoc);
  };

  getBaselineDocument = (type: SmartEditorType) => {
    return this.baselineDocuments.get(type);
  };

  hasDocumentChanges = (type: SmartEditorType) => {
    const baselineDoc = this.getBaselineDocument(type);
    const currentDoc = this.editorDocument.get(type);

    if (!baselineDoc || !currentDoc) {
      return false;
    }

    // Simple comparison using innerHTML
    return baselineDoc.body.innerHTML !== currentDoc.body.innerHTML;
  };

  getDocumentChanges = (type: SmartEditorType) => {
    // First check if we have tracked changes (much faster)
    const trackedChanges = this.getTrackedChanges(type);
    if (Object.keys(trackedChanges).length > 0) {
      return trackedChanges;
    }

    // Fall back to document comparison if no tracked changes
    const baselineDoc = this.getBaselineDocument(type);
    const currentDoc = this.editorDocument.get(type);

    if (!baselineDoc || !currentDoc) {
      return {};
    }

    const changes: Record<string, { old: string; updated: string }> = {};

    // Get all nodes with data-nodeid from current document
    const currentNodes = currentDoc.querySelectorAll('[data-nodeid]');

    currentNodes.forEach((currentNode) => {
      const nodeId = currentNode.getAttribute('data-nodeid');
      if (!nodeId) return;

      const baselineNode = baselineDoc.querySelector(`[data-nodeid="${nodeId}"]`);
      const currentContent = (currentNode as HTMLElement).textContent ?? '';
      const baselineContent = baselineNode ? ((baselineNode as HTMLElement).textContent ?? '') : '';

      // If content is different, record the change
      if (currentContent !== baselineContent) {
        changes[nodeId] = {
          old: baselineContent,
          updated: currentContent,
        };
      }
    });

    return changes;
  };

  resetToBaseline = (type: SmartEditorType) => {
    const baselineDoc = this.getBaselineDocument(type);
    if (baselineDoc) {
      // Create a fresh copy and set as current document
      const resetDoc = baselineDoc.cloneNode(true) as Document;
      this.editorDocument.set(type, resetDoc);
    }
  };

  /**
   * Update baseline after successful save operation
   * Call this after any successful save to update the comparison baseline
   *
   * @param type - Editor type (source/target)
   */
  updateBaselineAfterSave = (type: SmartEditorType) => {
    const currentDoc = this.editorDocument.get(type);
    if (currentDoc) {
      this.setBaselineDocument(type, currentDoc);
      // Clear tracked changes since baseline is now updated
      this.clearDocumentChanges(type);
    }
  };

  /**
   * Handle save operation - simplified version
   * Converts tracked changes directly to the legacy updatedCells format
   *
   * @param type - Editor type (source/target)
   * @returns Promise with converted suggestions ready for callSaveSuggestions
   */
  handleSave = async (type: SmartEditorType): Promise<SaveSuggestionPayload[]> => {
    const trackedChanges = this.getTrackedChanges(type);

    if (Object.keys(trackedChanges).length === 0) {
      return [];
    }

    const results: SaveSuggestionPayload[] = [];

    Object.entries(trackedChanges).forEach(([nodeId, change]) => {
      // Check if this node has an existing accepted suggestion
      const existingAcceptedSuggestion = this.acceptedSuggestions?.find(
        (s) => s.targetNodeId === nodeId,
      );

      if (existingAcceptedSuggestion) {
        // Case 1: Updating existing accepted suggestion
        // Full updated suggestion
        results.push({
          ...existingAcceptedSuggestion,
          targetValue: change.updated,
          oldTargetValue: change.old,
          updatedAt: new Date().toISOString(),
          sessionId: this.sessionId,
        });

        // Simple history entry
        results.push({
          mapperType: 'author',
          order: existingAcceptedSuggestion.order || existingAcceptedSuggestion.id,
          previousSuggestionId: existingAcceptedSuggestion.id,
          sessionId: this.sessionId,
          targetNodeId: nodeId,
          targetValue: change.updated,
        });
      } else {
        // Case 2: New suggestion for empty cell
        results.push({
          mapperType: 'author',
          order: undefined,
          previousSuggestionId: undefined,
          sessionId: this.sessionId,
          targetNodeId: nodeId,
          targetValue: change.updated,
        });
      }
    });

    return results;
  };

  // Incremental change tracking methods
  recordNodeChange = (
    type: SmartEditorType,
    nodeId: string,
    oldValue: string,
    newValue: string,
  ) => {
    const typeChanges = this.documentChanges.get(type) || {};
    typeChanges[nodeId] = { old: oldValue, updated: newValue };
    this.documentChanges.set(type, typeChanges);
  };

  clearDocumentChanges = (type: SmartEditorType) => {
    this.documentChanges.set(type, {});
  };

  /** Remove incremental tracking for one node (e.g. user reverted text to baseline). */
  clearTrackedNodeChange = (type: SmartEditorType, nodeId: string) => {
    const typeChanges = { ...(this.documentChanges.get(type) || {}) };
    delete typeChanges[nodeId];
    this.documentChanges.set(type, typeChanges);
  };

  getTrackedChanges = (type: SmartEditorType) => {
    return this.documentChanges.get(type) || {};
  };
}
